import {
  useAddGroupMember,
  useAssignGroupDashboard,
  useGroupDashboardIds,
  useGroupMembers,
  useRemoveGroupMember,
  useUnassignGroupDashboard,
} from "@/hooks/useUserGroups";
import { type PortalMember } from "@/hooks/useUsers";
import { Checkbox, Label } from "@simplicity/ui";
import { useState } from "react";

import { AddMemberDialog } from "./AddMemberDialog";
import { GroupDashboardsTable } from "./GroupDashboardsTable";
import { GroupMembersList } from "./GroupMembersList";
import { type GroupDashboard } from "./types";

// eslint-disable-next-line max-lines-per-function -- SIM-1760: 1 line over cap (91/90). Admin group-detail page with header + 2 sub-tables; splitting would add wrapper indirection without readability gain.
export function GroupDetail({
  groupId,
  isDefault,
  onSetDefault,
  users,
  dashboards,
  onError,
}: {
  groupId: string;
  isDefault: boolean;
  onSetDefault: (groupId: string | null) => void;
  users: PortalMember[];
  dashboards: GroupDashboard[];
  onError: (msg: string) => void;
}) {
  const { data: members } = useGroupMembers(groupId);
  const { data: assignedDashboardIds } = useGroupDashboardIds(groupId);
  const addMember = useAddGroupMember();
  const removeMember = useRemoveGroupMember();
  const assignDashboard = useAssignGroupDashboard();
  const unassignDashboard = useUnassignGroupDashboard();
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const emailMap = new Map(users.map((u) => [u.userId, u]));
  const memberUserIds = new Set(members.map((m) => m.userId));
  const availableUsers = users.filter((u) => !memberUserIds.has(u.userId));

  const handleAddMember = async (userId: string) => {
    try {
      await addMember.mutateAsync({ groupId, userId });
      setAddMemberOpen(false);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Failed to add member");
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await removeMember.mutateAsync({ groupId, userId });
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Failed to remove member");
    }
  };

  const handleToggleDashboard = async (dashboardId: string) => {
    try {
      if (assignedDashboardIds.includes(dashboardId)) {
        await unassignDashboard.mutateAsync({ dashboardId, groupId });
      } else {
        await assignDashboard.mutateAsync({ dashboardId, groupId });
      }
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Failed to update dashboard assignment");
    }
  };

  return (
    <div className="px-6 pb-4 space-y-4">
      <div className="flex items-center gap-2">
        <Checkbox
          id={`default-${groupId}`}
          checked={isDefault}
          onCheckedChange={() => onSetDefault(isDefault ? null : groupId)}
        />
        <Label htmlFor={`default-${groupId}`} className="text-xs cursor-pointer">
          Default group for new users
        </Label>
      </div>

      <GroupMembersList
        members={members}
        emailMap={emailMap}
        onRemoveMember={(userId) => {
          void handleRemoveMember(userId);
        }}
        onOpenAddMember={() => setAddMemberOpen(true)}
      />

      <GroupDashboardsTable
        groupId={groupId}
        dashboards={dashboards}
        assignedDashboardIds={assignedDashboardIds}
        onToggle={(dashboardId) => {
          void handleToggleDashboard(dashboardId);
        }}
      />

      <AddMemberDialog
        open={addMemberOpen}
        onOpenChange={setAddMemberOpen}
        availableUsers={availableUsers}
        isPending={addMember.isPending}
        onAdd={(userId) => {
          void handleAddMember(userId);
        }}
      />
    </div>
  );
}
